/**
 * Every candidate the screening dropped, and what it was dropped for.
 *
 * The invariant this renders is the whole reason the module screens rather than
 * filters in SQL: sendable plus every reason here adds back up to `examined`. A
 * campaign that says "4 000 companies" and mails 3 100 has lost 900 rows nobody
 * can account for, and the operator's only options are to trust it or to stop
 * using it. So the sum is checked on screen, and a mismatch is stated as a bug
 * in the screening rather than quietly rounded away.
 *
 * Sorted by size, not by the canonical reason order: on this card the question
 * is "what is eating my audience", and the answer is the biggest bar.
 */
import { useT } from "../../../../../i18n/I18nProvider";
import { SKIP_REASON_ORDER, type AudiencePreview, type SkipReason } from "../../../../../lib/marketing";
import { Bar, num, pct } from "../../shared";

export function DropReasons({ preview }: { preview: AudiencePreview }) {
  const t = useT();

  const dropped = SKIP_REASON_ORDER.map((r) => ({ reason: r as SkipReason, n: preview.skipped[r] ?? 0 }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n);

  const total = dropped.reduce((s, x) => s + x.n, 0);
  const accounted = preview.sendable + total;
  const balanced = accounted === preview.examined;

  if (dropped.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
        {t("marketing.preview.nothingDropped")}
      </p>
    );
  }

  return (
    <div className="space-y-2.5">
      <h4 className="flex items-baseline justify-between gap-2 text-xs font-semibold">
        {t("marketing.preview.whyNot", { n: num(total) })}
      </h4>

      <ul className="space-y-2.5">
        {dropped.map(({ reason, n }) => (
          <li key={reason} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-xs">{t(`marketing.skip.${reason}`)}</span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span className="font-mono text-[10px] text-muted-foreground/60">{pct(n, total)}</span>
                <span className="font-mono text-xs tabular-nums">{num(n)}</span>
              </span>
            </div>
            <Bar value={n} total={total} tone="warn" />
            <p className="text-[10px] leading-snug text-muted-foreground/70">{t(`marketing.skipWhy.${reason}`)}</p>
          </li>
        ))}
      </ul>

      {!balanced && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-[11px] text-destructive">
          {t("marketing.preview.unbalanced", { accounted: num(accounted), examined: num(preview.examined) })}
        </p>
      )}
    </div>
  );
}
